// pages/SecurityDepositDashboard.jsx
// Admin view of security_deposit_bookings - one row per booth deposit.

import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import API from '../api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  FiShield, FiCheckCircle, FiClock, FiXCircle, FiRefreshCw,
  FiCopy, FiExternalLink, FiChevronDown, FiChevronUp, FiDownload, FiCornerUpLeft, FiTrash2, FiEdit2,
} from 'react-icons/fi';

const fmt   = (n)  => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDt = (dt) => dt ? new Date(dt).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '-';
const fmtDateOnly = (dt) => dt ? new Date(dt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';

// Base URL for receipt files served statically by the backend
// (adjust if your REACT_APP_API_URL doesn't end in /api)
const RECEIPT_BASE_URL = (process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://paymentsapi.steponexp.com').replace(/\/$/, '');

const STATUS = {
  SUCCESS: { bg: 'rgba(0,214,143,.12)',  color: '#00a86b', label: '✅ Paid'     },
  PENDING: { bg: 'rgba(245,166,35,.12)', color: '#d4900a', label: '⏳ Pending'  },
  FAILED:  { bg: 'rgba(255,71,87,.12)',  color: '#c0392b', label: '❌ Failed'   },
  REFUNDED:{ bg: 'rgba(100,100,200,.12)',color: '#5555aa', label: '↩ Refunded' },
};

const REFUND_STATUS = {
  NOT_INITIATED: { bg: 'rgba(176,176,192,.15)', color: '#6b6b80', label: 'Not initiated' },
  INITIATED:     { bg: 'rgba(124,92,252,.12)',  color: '#7c5cfc', label: 'Refund initiated' },
  COMPLETED:     { bg: 'rgba(0,214,143,.12)',   color: '#00a86b', label: 'Refunded' },
};

// Verification status — separate from payment_status. Tracks whether the
// customer has submitted a receipt for manual bank-transfer/UPI review yet.
const VERIFICATION = {
  PENDING:   { bg: 'rgba(176,176,192,.15)', color: '#6b6b80', label: '— Not submitted' },
  SUBMITTED: { bg: 'rgba(245,166,35,.12)',  color: '#d4900a', label: '⏳ Awaiting verification' },
  VERIFIED:  { bg: 'rgba(0,214,143,.12)',   color: '#00a86b', label: '✅ Verified' },
  REJECTED:  { bg: 'rgba(255,71,87,.12)',   color: '#c0392b', label: '❌ Rejected' },
};

function Chip({ status, map = STATUS }) {
  const s = map[status] || map.PENDING || Object.values(map)[0];
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:14, padding:'18px 22px', display:'flex', alignItems:'center', gap:16 }}>
      <div style={{ width:46, height:46, borderRadius:12, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, color, flexShrink:0 }}>{icon}</div>
      <div>
        <div style={{ fontSize:22, fontWeight:800, color:'#1a1a2e', lineHeight:1 }}>{value}</div>
        <div style={{ fontSize:12, color:'#6b6b80', marginTop:3 }}>{label}</div>
        {sub && <div style={{ fontSize:12, fontWeight:600, color, marginTop:2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Edit form field helpers ──────────────────────────────────────────────
const fieldStyle = {
  width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8,
  border: '1px solid #dcdce6', fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

function Field({ label, value, onChange, type = 'text', span }) {
  return (
    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#4a4a5e', gridColumn: span ? 'span 2' : undefined }}>
      {label}
      <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} style={fieldStyle} />
    </label>
  );
}

// Builds a blank/prefilled edit form from a booking row.
function bookingToEditForm(b) {
  return {
    merchantTransactionId: b.merchant_transaction_id,

    boothNo: b.booth_no || '',
    boothName: b.booth_name || '',

    companyName: b.company_name || '',
    companyAddress: b.company_address || '',
    companyGst: b.company_gst || '',
    companyPocPhone: b.company_poc_phone || '',
    companyPocEmail: b.company_poc_email || '',

    hasVendor: !!b.has_vendor,
    vendorName: b.vendor_name || '',
    vendorAddress: b.vendor_address || '',
    vendorGst: b.vendor_gst || '',
    vendorPocPhone: b.vendor_poc_phone || '',
    vendorPocEmail: b.vendor_poc_email || '',

    bankName: b.bank_name || '',
    bankIfsc: b.bank_ifsc || '',
    bankAccountName: b.bank_account_name || '',
    bankAccountNumber: b.bank_account_number || '',
    bankAddress: b.bank_address || '',

    depositAmount: b.deposit_amount ?? '',
  };
}

export default function SecurityDepositDashboard() {
  const [stats,    setStats]    = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('ALL');
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [marking, setMarking] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [verifying, setVerifying] = useState(null);

  // ── Delete: selection + in-flight state ─────────────────────────────────
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // ── Edit: modal + form state ─────────────────────────────────────────────
  const [editForm, setEditForm] = useState(null); // null = modal closed
  const [savingEdit, setSavingEdit] = useState(false);
  const setEditField = (key, value) => setEditForm(prev => ({ ...prev, [key]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/razorpay/security-deposit-stats');
      if (!data.success) throw new Error(data.message);
      setStats(data.stats);
      setBookings(data.bookings || []);
    } catch (err) {
      toast.error('Failed to load security deposit data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncAll = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const { data } = await API.post('/razorpay/sync-security-deposit');
      if (!data.success) throw new Error(data.message);
      const updated = data.results?.filter(r => r.status === 'SUCCESS').length || 0;
      setSyncMsg(updated > 0
        ? `✅ ${updated} payment${updated > 1 ? 's' : ''} marked as paid`
        : `✓ Checked ${data.checked} booking${data.checked !== 1 ? 's' : ''} - all up to date`
      );
      if (updated > 0) await load();
    } catch (err) {
      setSyncMsg('❌ Sync failed - ' + (err.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const markAsPaid = async (b) => {
    const transactionReference = window.prompt(
      `Enter the bank UTR / Razorpay payment ID for this deposit\n\nLeave blank to mark paid without a reference:`,
      ''
    );
    if (transactionReference === null) return;
    setMarking(b.merchant_transaction_id);
    try {
      const { data } = await API.post('/razorpay/mark-security-deposit-paid', {
        merchantTransactionId: b.merchant_transaction_id,
        transactionReference: transactionReference.trim() || null,
      });
      if (!data.success) throw new Error(data.message);
      toast.success('Marked as paid ✅');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setMarking(null);
    }
  };

  // ── Verify / reject a submitted bank-transfer or UPI receipt ──────────────
  // NOTE: securityDepositBankTransferController.verifySecurityDepositReceipt
  // expects { merchantTransactionId, status: 'VERIFIED' | 'REJECTED', notes }
  // — adjust the URL below to match wherever you mounted that route in
  // routes/index.js (e.g. '/security-deposit-bank/verify-receipt').
  const verifyReceipt = async (b, status) => {
    const notes = status === 'REJECTED'
      ? window.prompt('Reason for rejecting this receipt (optional):', '') || ''
      : '';
    setVerifying(b.merchant_transaction_id);
    try {
      const { data } = await API.post('/security-deposit-bank/verify-receipt', {
        merchantTransactionId: b.merchant_transaction_id,
        status,
        notes,
      });
      if (!data.success) throw new Error(data.message);
      toast.success(status === 'VERIFIED' ? 'Marked as verified ✅' : 'Receipt rejected');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setVerifying(null);
    }
  };

  const initiateRefund = async (b) => {
    const confirmMsg = `Refund ₹${Number(b.deposit_amount).toLocaleString('en-IN')} to ${b.company_name}?\n\nThis will call Razorpay's refund API immediately (or mark it initiated for manual bank transfer).`;
    if (!window.confirm(confirmMsg)) return;

    const notes = window.prompt('Optional note for this refund (e.g. "Booth cleared, no damages"):', '') || '';

    setRefunding(b.merchant_transaction_id);
    try {
      const { data } = await API.post('/razorpay/security-deposit-refund', {
        merchantTransactionId: b.merchant_transaction_id,
        refundAmount: b.deposit_amount,
        notes,
      });
      if (!data.success) throw new Error(data.message);
      toast.success(data.message || 'Refund initiated ✅');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setRefunding(null);
    }
  };

  const markRefundCompleted = async (b) => {
    if (!window.confirm(`Mark refund for ${b.company_name} as completed?`)) return;
    const refundReference = window.prompt('Bank UTR / reference for this refund (optional):', '') || '';
    setRefunding(b.merchant_transaction_id);
    try {
      const { data } = await API.post('/razorpay/security-deposit-refund-done', {
        merchantTransactionId: b.merchant_transaction_id,
        refundReference: refundReference.trim() || null,
      });
      if (!data.success) throw new Error(data.message);
      toast.success('Refund marked as completed ✅');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setRefunding(null);
    }
  };

  // ── Delete: single + bulk ───────────────────────────────────────────────
  // Backend route deletes the security_deposit_bookings row(s) AND their
  // paired transactions row(s) — see controllers/securityDepositBankTransferController.js
  const deleteBookings = async (merchantTransactionIds) => {
    setDeleting(true);
    try {
      const { data } = await API.post('/security-deposit/delete-security-deposits', { merchantTransactionIds });
      if (!data.success) throw new Error(data.message);
      toast.success(`Deleted ${data.deleted} booking${data.deleted !== 1 ? 's' : ''}`);
      setSelected(prev => {
        const next = new Set(prev);
        merchantTransactionIds.forEach(id => next.delete(id));
        return next;
      });
      setExpanded(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const deleteOne = (b) => {
    if (!window.confirm(`Delete this booking permanently?\n\n${b.company_name} — ${b.merchant_transaction_id}\n\nThis cannot be undone.`)) return;
    deleteBookings([b.merchant_transaction_id]);
  };

  const deleteSelected = () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected booking${selected.size !== 1 ? 's' : ''} permanently?\n\nThis cannot be undone.`)) return;
    deleteBookings(Array.from(selected));
  };

  // ── Edit: open modal, save modal ────────────────────────────────────────
  // Backend route: POST /security-deposit/update-security-deposit
  // handled by securityDepositBankTransferController.updateSecurityDepositBooking
  const openEdit = (b) => setEditForm(bookingToEditForm(b));
  const closeEdit = () => { if (!savingEdit) setEditForm(null); };

  const saveEdit = async () => {
    if (!editForm) return;

    if (!editForm.boothNo?.trim() || !editForm.boothName?.trim() || !editForm.companyName?.trim()) {
      toast.error('Booth No, Booth Name and Company Name are required');
      return;
    }
    if (!editForm.depositAmount || isNaN(parseFloat(editForm.depositAmount))) {
      toast.error('Enter a valid deposit amount');
      return;
    }

    setSavingEdit(true);
    try {
      const { data } = await API.post('/security-deposit/update-security-deposit', {
        merchantTransactionId: editForm.merchantTransactionId,

        boothNo: editForm.boothNo.trim(),
        boothName: editForm.boothName.trim(),

        companyDetails: {
          name: editForm.companyName.trim(),
          address: editForm.companyAddress.trim() || null,
          gst: editForm.companyGst.trim() || null,
          pocPhone: editForm.companyPocPhone.trim() || null,
          pocEmail: editForm.companyPocEmail.trim() || null,
        },

        vendorDetails: editForm.hasVendor ? {
          name: editForm.vendorName.trim() || null,
          address: editForm.vendorAddress.trim() || null,
          gst: editForm.vendorGst.trim() || null,
          pocPhone: editForm.vendorPocPhone.trim() || null,
          pocEmail: editForm.vendorPocEmail.trim() || null,
        } : null,

        bankDetails: {
          bankName: editForm.bankName.trim() || null,
          ifsc: editForm.bankIfsc.trim() || null,
          accountName: editForm.bankAccountName.trim() || null,
          accountNumber: editForm.bankAccountNumber.trim() || null,
          address: editForm.bankAddress.trim() || null,
        },

        depositAmount: parseFloat(editForm.depositAmount),
      });

      if (!data.success) throw new Error(data.message);
      toast.success('Booking updated ✅');
      setEditForm(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const filtered = bookings.filter(b => {
    const ok = filter === 'ALL' || b.payment_status === filter;
    const q  = search.toLowerCase();
    return ok && (!q ||
      b.company_name?.toLowerCase().includes(q) ||
      b.booth_no?.toLowerCase().includes(q) ||
      b.booth_name?.toLowerCase().includes(q) ||
      b.company_poc_phone?.includes(q) ||
      b.company_poc_email?.toLowerCase().includes(q) ||
      b.company_gst?.toLowerCase().includes(q) ||
      b.merchant_transaction_id?.toLowerCase().includes(q)
    );
  });

  // ── Delete: selection helpers (depend on `filtered`, so defined after it) ──
  const allVisibleSelected = filtered.length > 0 && filtered.every(b => selected.has(b.merchant_transaction_id));

  const toggleOne = (txnId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(txnId)) next.delete(txnId);
      else next.add(txnId);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach(b => next.delete(b.merchant_transaction_id));
      else filtered.forEach(b => next.add(b.merchant_transaction_id));
      return next;
    });
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };
  const s = stats || {};

  // ── XLSX Export ──────────────────────────────────────────────────────────
  const exportToXlsx = () => {
    if (filtered.length === 0) {
      toast.error('No bookings to export');
      return;
    }

    const rows = filtered.map((b) => ({
      'Date':                fmtDt(b.created_at),
      'Booth No':            b.booth_no,
      'Booth Name':          b.booth_name,
      'Event':               b.event_name || '',
      'Company Name':        b.company_name,
      'Company GST':         b.company_gst || '',
      'Company Address':     b.company_address || '',
      'Company POC Phone':   b.company_poc_phone,
      'Company POC Email':   b.company_poc_email,
      'Has Vendor':          b.has_vendor ? 'Yes' : 'No',
      'Vendor Name':         b.vendor_name || '',
      'Vendor GST':          b.vendor_gst || '',
      'Vendor Address':      b.vendor_address || '',
      'Vendor POC Phone':    b.vendor_poc_phone || '',
      'Vendor POC Email':    b.vendor_poc_email || '',
      'Bank Name':           b.bank_name,
      'IFSC Code':           b.bank_ifsc,
      'Account Holder Name': b.bank_account_name,
      'Account Number':      b.bank_account_number,
      'Bank Address':        b.bank_address || '',
      'Deposit Amount':      Number(b.deposit_amount),
      'Payment Method':      b.payment_method || '',
      'UTR Number':          b.utr_number || '',
      'Payment Status':      b.payment_status,
      'Verification Status': VERIFICATION[b.verification_status]?.label || 'Not submitted',
      'Razorpay Payment ID': b.razorpay_payment_id || '',
      'Transaction ID':      b.merchant_transaction_id,
      'Refund Status':       REFUND_STATUS[b.refund_status]?.label || 'Not initiated',
      'Refund Amount':       b.refund_amount != null ? Number(b.refund_amount) : '',
      'Refund ID':           b.refund_id || '',
      'Refund Initiated':    fmtDt(b.refund_initiated_at),
      'Refund Completed':    fmtDt(b.refund_completed_at),
      'Refund Notes':        b.refund_notes || '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Reasonable column widths so it's readable without manual resizing
    ws['!cols'] = [
      { wch: 18 }, { wch: 10 }, { wch: 24 }, { wch: 22 }, { wch: 16 },
      { wch: 28 }, { wch: 14 }, { wch: 24 }, { wch: 9 }, { wch: 20 },
      { wch: 16 }, { wch: 28 }, { wch: 14 }, { wch: 24 }, { wch: 18 },
      { wch: 13 }, { wch: 20 }, { wch: 18 }, { wch: 28 }, { wch: 14 },
      { wch: 16 }, { wch: 20 }, { wch: 12 }, { wch: 24 }, { wch: 20 },
      { wch: 22 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 18 },
      { wch: 18 }, { wch: 30 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Security Deposits');

    const filterLabel = filter === 'ALL' ? 'all' : filter.toLowerCase();
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `security-deposits-${filterLabel}-${dateStr}.xlsx`);

    toast.success(`Exported ${rows.length} booking${rows.length !== 1 ? 's' : ''}`);
  };

  return (
    <Layout>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="page-title" style={{ display:'flex', alignItems:'center', gap:10 }}>
            <FiShield style={{ color:'#7c5cfc' }} /> Security Deposit Bookings
          </h1>
          <p className="page-subtitle">Booth-wise refundable deposits from the public form</p>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <button className="btn btn-outline btn-sm" onClick={load} disabled={loading || syncing}>
              <FiRefreshCw style={{ marginRight:6 }} />{loading ? 'Loading…' : 'Refresh'}
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={syncAll}
              disabled={syncing || loading}
              style={{ background:'#00a86b', borderColor:'#00a86b' }}
              title="Only syncs legacy Razorpay bookings — bank transfer/UPI receipts are verified manually below"
            >
              <FiRefreshCw style={{ marginRight:6, animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
              {syncing ? 'Syncing with Razorpay…' : 'Sync Razorpay Statuses'}
            </button>
            <button
              className="btn btn-sm"
              onClick={exportToXlsx}
              disabled={loading || filtered.length === 0}
              style={{ background:'#1a1a2e', color:'#fff', border:'none' }}
            >
              <FiDownload style={{ marginRight:6 }} />Export XLSX
            </button>
          </div>
          {syncMsg && (
            <span style={{ fontSize:12, color: syncMsg.startsWith('✅') ? '#00a86b' : syncMsg.startsWith('✓') ? '#6b6b80' : '#c0392b' }}>
              {syncMsg}
            </span>
          )}
        </div>
      </div>

      <div className="page-body fade-in">

        {/* Stat cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))', gap:16, marginBottom:28 }}>
          <StatCard icon={<FiShield />}      label="Total deposits"     value={s.total?.count ?? '-'} sub={s.total?.amount ? fmt(s.total.amount) + ' requested' : null} color="#7c5cfc" />
          <StatCard icon={<FiCheckCircle />} label="Paid"               value={s.paid?.count  ?? '-'} sub={s.paid?.amount  ? fmt(s.paid.amount)  + ' collected' : null} color="#00a86b" />
          <StatCard icon={<FiClock />}       label="Awaiting payment"   value={s.pending?.count ?? '-'} color="#d4900a" />
          <StatCard icon={<FiClock />}       label="Awaiting verification" value={s.submitted?.count ?? '-'} sub="Receipt submitted, needs review" color="#d4900a" />
          <StatCard icon={<FiXCircle />}     label="Failed / Rejected"  value={s.failed?.count  ?? '-'} color="#c0392b" />
          <StatCard icon={<FiCornerUpLeft />} label="Refund due"        value={s.refundDue?.count ?? '-'} sub="Paid, not yet refunded" color="#d4900a" />
          <StatCard icon={<FiCornerUpLeft />} label="Refunded"          value={s.refunded?.count ?? '-'} sub={s.refunded?.amount ? fmt(s.refunded.amount) + ' returned' : null} color="#5555aa" />
        </div>

        {/* Filter + search */}
        <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ display:'flex', gap:6 }}>
            {['ALL','SUCCESS','PENDING','FAILED'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', border:'1px solid',
                borderColor: filter === f ? '#1a1a2e' : '#dcdce6',
                background:  filter === f ? '#1a1a2e' : '#fff',
                color:       filter === f ? '#fff'    : '#4a4a5e',
              }}>
                {f === 'ALL' ? 'All' : STATUS[f]?.label}
              </button>
            ))}
          </div>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search company, booth, phone, GST, email, txn ID…"
            style={{ flex:1, minWidth:220, padding:'8px 12px', borderRadius:10, border:'1px solid #dcdce6', fontSize:13, outline:'none' }}
          />
        </div>

        {/* ── Delete: bulk action bar — only shows once something's selected ── */}
        {selected.size > 0 && (
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap',
            background:'#1a1a2e', color:'#fff', borderRadius:12, padding:'12px 18px', marginBottom:16,
          }}>
            <span style={{ fontSize:13, fontWeight:600 }}>{selected.size} selected</span>
            <div style={{ display:'flex', gap:10 }}>
              <button
                onClick={() => setSelected(new Set())}
                style={{ background:'transparent', border:'1px solid rgba(255,255,255,.3)', color:'#fff', borderRadius:8, padding:'8px 14px', fontSize:12, fontWeight:600, cursor:'pointer' }}
              >
                Clear
              </button>
              <button
                onClick={deleteSelected}
                disabled={deleting}
                style={{ background:'#c0392b', border:'none', color:'#fff', borderRadius:8, padding:'8px 16px', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, opacity: deleting ? 0.6 : 1 }}
              >
                <FiTrash2 />{deleting ? 'Deleting…' : `Delete Selected (${selected.size})`}
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:48, color:'#b0b0c0', fontSize:14 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:48, color:'#b0b0c0', fontSize:14 }}>
              {bookings.length === 0 ? 'No security deposit bookings yet.' : 'No results for this filter.'}
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#f8f8fc', borderBottom:'1px solid #ececf2' }}>
                    {/* ── Delete: select-all checkbox column ── */}
                    <th style={{ padding:'11px 14px', width:36 }}>
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} onClick={e => e.stopPropagation()} />
                    </th>
                    {['Date','Booth','Booth Name','Company','GST','Phone / Email','Amount','Status','Verification','Refund',''].map(h => (
                      <th key={h} style={{ padding:'11px 14px', textAlign:'left', fontWeight:700, color:'#6b6b80', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b, i) => {
                    const isOpen = expanded === b.merchant_transaction_id;

                    return (
                      <>
                        <tr
                          key={b.merchant_transaction_id}
                          onClick={() => setExpanded(isOpen ? null : b.merchant_transaction_id)}
                          style={{ borderBottom:'1px solid #f0f0f5', cursor:'pointer', background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                        >
                          {/* ── Delete: per-row checkbox ── */}
                          <td style={{ padding:'11px 14px' }} onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selected.has(b.merchant_transaction_id)}
                              onChange={() => toggleOne(b.merchant_transaction_id)}
                            />
                          </td>

                          <td style={{ padding:'11px 14px', whiteSpace:'nowrap', color:'#6b6b80' }}>{fmtDt(b.created_at)}</td>

                          <td style={{ padding:'11px 14px' }}>
                            <span style={{ background:'#f1f1f6', borderRadius:8, padding:'3px 10px', fontWeight:700, fontSize:12 }}>
                              {b.booth_no}
                            </span>
                          </td>
                          <td style={{ padding:'11px 14px', maxWidth:160, color:'#4a4a5e' }}>
                            {b.booth_name || '-'}
                          </td>

                          <td style={{ padding:'11px 14px', maxWidth:180 }}>
                            <div style={{ fontWeight:700, color:'#1a1a2e' }}>{b.company_name}</div>
                            {b.has_vendor && b.vendor_name && (
                              <div style={{ fontSize:11, color:'#b0b0c0', marginTop:2 }}>Vendor: {b.vendor_name}</div>
                            )}
                          </td>

                          <td style={{ padding:'11px 14px', color:'#4a4a5e', fontFamily:'monospace', fontSize:12 }}>
                            {b.company_gst || '-'}
                          </td>

                          <td style={{ padding:'11px 14px' }}>
                            <div style={{ fontWeight:600, color:'#1a1a2e' }}>{b.company_poc_phone}</div>
                            {b.company_poc_email && <div style={{ fontSize:11, color:'#b0b0c0' }}>{b.company_poc_email}</div>}
                          </td>

                          <td style={{ padding:'11px 14px', whiteSpace:'nowrap' }}>
                            <div style={{ fontWeight:800, color:'#1a1a2e' }}>{fmt(b.deposit_amount)}</div>
                          </td>

                          <td style={{ padding:'11px 14px' }}><Chip status={b.payment_status} /></td>

                          <td style={{ padding:'11px 14px' }}><Chip status={b.verification_status} map={VERIFICATION} /></td>

                          <td style={{ padding:'11px 14px' }}>
                            {b.payment_status === 'SUCCESS'
                              ? <Chip status={b.refund_status} map={REFUND_STATUS} />
                              : <span style={{ color:'#dcdce6', fontSize:12 }}>-</span>}
                          </td>

                          <td style={{ padding:'11px 14px', color:'#7c5cfc', fontWeight:700, fontSize:12 }}>
                            {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                          </td>
                        </tr>

                        {/* ── Expanded detail row ── */}
                        {isOpen && (
                          <tr style={{ background:'#f8f8fc', borderBottom:'1px solid #e8e8f0' }}>
                            {/* ── Delete: colSpan bumped from 10 to 11 for the new checkbox column ── */}
                            <td colSpan={11} style={{ padding:'16px 20px' }}>
                              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom: 10 }}>
                                <button
                                  className="btn btn-sm"
                                  onClick={(e) => { e.stopPropagation(); openEdit(b); }}
                                  style={{ background:'#7c5cfc', color:'#fff', border:'none' }}
                                >
                                  <FiEdit2 style={{ marginRight:4 }} />Edit booking
                                </button>
                              </div>
                              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:16 }}>

                                {/* Company card */}
                                <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:12, padding:'14px 16px' }}>
                                  <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>Company details</div>
                                  {[
                                    ['Booth No',  b.booth_no],
                                    ['Booth Name', b.booth_name || '-'],
                                    ['Name',    b.company_name],
                                    ['GST',     b.company_gst || '-'],
                                    ['Address', b.company_address || '-'],
                                    ['POC Phone', b.company_poc_phone],
                                    ['POC Email', b.company_poc_email],
                                  ].map(([lbl, val]) => (
                                    <div key={lbl} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'5px 0', borderBottom:'1px solid #f5f5fa', fontSize:13 }}>
                                      <span style={{ color:'#b0b0c0', fontWeight:600, flexShrink:0 }}>{lbl}</span>
                                      <span style={{ color:'#1a1a2e', fontWeight:600, textAlign:'right', wordBreak:'break-all' }}>{val}</span>
                                    </div>
                                  ))}
                                </div>

                                {/* Vendor card */}
                                <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:12, padding:'14px 16px' }}>
                                  <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>Vendor details</div>
                                  {b.has_vendor ? (
                                    [
                                      ['Name',      b.vendor_name || '-'],
                                      ['GST',       b.vendor_gst || '-'],
                                      ['Address',   b.vendor_address || '-'],
                                      ['POC Phone', b.vendor_poc_phone || '-'],
                                      ['POC Email', b.vendor_poc_email || '-'],
                                    ].map(([lbl, val]) => (
                                      <div key={lbl} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'5px 0', borderBottom:'1px solid #f5f5fa', fontSize:13 }}>
                                        <span style={{ color:'#b0b0c0', fontWeight:600, flexShrink:0 }}>{lbl}</span>
                                        <span style={{ color:'#1a1a2e', fontWeight:600, textAlign:'right', wordBreak:'break-all' }}>{val}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <div style={{ fontSize:13, color:'#b0b0c0' }}>No vendor added for this booking.</div>
                                  )}
                                </div>

                                {/* Bank / refund card */}
                                <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:12, padding:'14px 16px' }}>
                                  <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>Bank details (refund)</div>
                                  {[
                                    ['Bank',      b.bank_name],
                                    ['IFSC',      b.bank_ifsc],
                                    ['Acc. Name', b.bank_account_name],
                                    ['Acc. No.',  b.bank_account_number],
                                    ['Branch',    b.bank_address || '-'],
                                  ].map(([lbl, val]) => (
                                    <div key={lbl} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'5px 0', borderBottom:'1px solid #f5f5fa', fontSize:13 }}>
                                      <span style={{ color:'#b0b0c0', fontWeight:600, flexShrink:0 }}>{lbl}</span>
                                      <span style={{ color:'#1a1a2e', fontWeight:600, textAlign:'right', wordBreak:'break-all' }}>{val}</span>
                                    </div>
                                  ))}
                                </div>

                                {/* Payment + refund status card */}
                                <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:12, padding:'14px 16px' }}>
                                  <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>Payment & refund</div>
                                  {[
                                    ['Status',      <Chip status={b.payment_status} />],
                                    ['Method',      b.payment_method === 'UPI_QR' ? 'UPI / GPay QR' : b.payment_method === 'BANK_TRANSFER' ? 'Bank transfer' : '—'],
                                    ['Txn ID',      <span style={{ fontFamily:'monospace', fontSize:11 }}>{b.merchant_transaction_id}</span>],
                                    ['Razorpay ID', b.razorpay_payment_id || '-'],
                                    ['Refund status', <Chip status={b.refund_status} map={REFUND_STATUS} />],
                                    ...(b.refund_amount ? [['Refund amount', fmt(b.refund_amount)]] : []),
                                    ...(b.refund_initiated_at ? [['Refund initiated', fmtDateOnly(b.refund_initiated_at)]] : []),
                                    ...(b.refund_completed_at ? [['Refund completed', fmtDateOnly(b.refund_completed_at)]] : []),
                                    ...(b.refund_notes ? [['Notes', b.refund_notes]] : []),
                                  ].map(([lbl, val]) => (
                                    <div key={lbl} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'5px 0', borderBottom:'1px solid #f5f5fa', fontSize:13 }}>
                                      <span style={{ color:'#b0b0c0', fontWeight:600, flexShrink:0 }}>{lbl}</span>
                                      <span style={{ color:'#1a1a2e', fontWeight:600, textAlign:'right' }}>{val}</span>
                                    </div>
                                  ))}

                                  {/* Receipt viewer + UTR */}
                                  {b.receipt_path && (
                                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e8e8f0' }}>
                                      <a
                                        className="btn btn-outline btn-sm"
                                        href={`${RECEIPT_BASE_URL}${b.receipt_path.startsWith('/') ? '' : '/'}${b.receipt_path}`}
                                        target="_blank" rel="noreferrer"
                                        style={{ textDecoration: 'none' }}
                                      >
                                        <FiExternalLink style={{ marginRight: 4 }} />View receipt
                                      </a>
                                      {b.utr_number && (
                                        <span style={{ marginLeft: 10, fontSize: 12, color: '#6b6b80' }}>
                                          UTR: <span style={{ fontFamily: 'monospace' }}>{b.utr_number}</span>
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  <div style={{ marginTop:12, display:'flex', gap:8, flexWrap:'wrap' }}>
                                    {b.payment_link && (
                                      <>
                                        <button className="btn btn-outline btn-sm" onClick={() => copy(b.payment_link)}><FiCopy style={{ marginRight:4 }} />Copy link</button>
                                        <a className="btn btn-outline btn-sm" href={b.payment_link} target="_blank" rel="noreferrer" style={{ textDecoration:'none' }}><FiExternalLink style={{ marginRight:4 }} />Open</a>
                                      </>
                                    )}

                                    {/* Verify / Reject — only while a receipt is awaiting review */}
                                    {b.verification_status === 'SUBMITTED' && (
                                      <>
                                        <button
                                          className="btn btn-sm"
                                          style={{ background:'#00a86b', color:'#fff', border:'none', opacity: verifying === b.merchant_transaction_id ? 0.6 : 1 }}
                                          onClick={() => verifyReceipt(b, 'VERIFIED')}
                                          disabled={verifying === b.merchant_transaction_id}
                                        >
                                          {verifying === b.merchant_transaction_id ? 'Saving…' : '✅ Verify payment'}
                                        </button>
                                        <button
                                          className="btn btn-sm"
                                          style={{ background:'#c0392b', color:'#fff', border:'none', opacity: verifying === b.merchant_transaction_id ? 0.6 : 1 }}
                                          onClick={() => verifyReceipt(b, 'REJECTED')}
                                          disabled={verifying === b.merchant_transaction_id}
                                        >
                                          ❌ Reject
                                        </button>
                                      </>
                                    )}

                                    {/* Manual override — for cases with no receipt submitted yet */}
                                    {b.payment_status === 'PENDING' && b.verification_status !== 'SUBMITTED' && (
                                      <button
                                        className="btn btn-sm"
                                        style={{ background:'#00a86b', color:'#fff', border:'none', opacity: marking === b.merchant_transaction_id ? 0.6 : 1 }}
                                        onClick={() => markAsPaid(b)}
                                        disabled={marking === b.merchant_transaction_id}
                                      >
                                        {marking === b.merchant_transaction_id ? 'Saving…' : '✅ Mark as Paid'}
                                      </button>
                                    )}

                                    {b.payment_status === 'SUCCESS' && b.refund_status === 'NOT_INITIATED' && (
                                      <button
                                        className="btn btn-sm"
                                        style={{ background:'#7c5cfc', color:'#fff', border:'none', opacity: refunding === b.merchant_transaction_id ? 0.6 : 1 }}
                                        onClick={() => initiateRefund(b)}
                                        disabled={refunding === b.merchant_transaction_id}
                                      >
                                        <FiCornerUpLeft style={{ marginRight:4 }} />
                                        {refunding === b.merchant_transaction_id ? 'Processing…' : 'Initiate Refund'}
                                      </button>
                                    )}

                                    {b.payment_status === 'SUCCESS' && b.refund_status === 'INITIATED' && (
                                      <button
                                        className="btn btn-sm"
                                        style={{ background:'#00a86b', color:'#fff', border:'none', opacity: refunding === b.merchant_transaction_id ? 0.6 : 1 }}
                                        onClick={() => markRefundCompleted(b)}
                                        disabled={refunding === b.merchant_transaction_id}
                                      >
                                        {refunding === b.merchant_transaction_id ? 'Saving…' : '✅ Mark Refund Completed'}
                                      </button>
                                    )}
                                  </div>

                                  {/* ── Delete + Edit: single-row action buttons ── */}
                                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e8e8f0', display:'flex', gap:8, flexWrap:'wrap' }}>
                                    <button
                                      className="btn btn-sm"
                                      style={{ background:'#7c5cfc', color:'#fff', border:'none' }}
                                      onClick={() => openEdit(b)}
                                    >
                                      <FiEdit2 style={{ marginRight:4 }} />Edit booking
                                    </button>
                                    <button
                                      className="btn btn-sm"
                                      style={{ background:'#c0392b', color:'#fff', border:'none', opacity: deleting ? 0.6 : 1 }}
                                      onClick={() => deleteOne(b)}
                                      disabled={deleting}
                                    >
                                      <FiTrash2 style={{ marginRight:4 }} />Delete booking
                                    </button>
                                  </div>
                                </div>

                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p style={{ marginTop:12, fontSize:12, color:'#b0b0c0' }}>
          Showing {filtered.length} of {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
        </p>

      </div>

      {/* ── Edit modal ─────────────────────────────────────────────────── */}
      {editForm && (
        <div
          onClick={closeEdit}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,20,30,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16, padding: 24, width: 640,
              maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.25)',
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 4, fontSize: 18, fontWeight: 800, color: '#1a1a2e', display:'flex', alignItems:'center', gap:8 }}>
              <FiEdit2 style={{ color:'#7c5cfc' }} /> Edit Security Deposit Booking
            </h3>
            <p style={{ margin: 0, marginBottom: 18, fontSize: 12.5, color: '#6b6b80', fontFamily:'monospace' }}>
              {editForm.merchantTransactionId}
            </p>

            <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Booth</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <Field label="Booth No" value={editForm.boothNo} onChange={v => setEditField('boothNo', v)} />
              <Field label="Booth Name" value={editForm.boothName} onChange={v => setEditField('boothName', v)} />
            </div>

            <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Company details</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <Field label="Company Name" value={editForm.companyName} onChange={v => setEditField('companyName', v)} span />
              <Field label="GST Number" value={editForm.companyGst} onChange={v => setEditField('companyGst', v)} />
              <Field label="Address" value={editForm.companyAddress} onChange={v => setEditField('companyAddress', v)} />
              <Field label="POC Phone" value={editForm.companyPocPhone} onChange={v => setEditField('companyPocPhone', v)} />
              <Field label="POC Email" value={editForm.companyPocEmail} onChange={v => setEditField('companyPocEmail', v)} />
            </div>

            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em' }}>Vendor details</div>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600, color:'#4a4a5e', cursor:'pointer' }}>
                <input type="checkbox" checked={editForm.hasVendor} onChange={e => setEditField('hasVendor', e.target.checked)} />
                Has vendor
              </label>
            </div>
            {editForm.hasVendor && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                <Field label="Vendor Name" value={editForm.vendorName} onChange={v => setEditField('vendorName', v)} span />
                <Field label="Vendor GST" value={editForm.vendorGst} onChange={v => setEditField('vendorGst', v)} />
                <Field label="Vendor Address" value={editForm.vendorAddress} onChange={v => setEditField('vendorAddress', v)} />
                <Field label="Vendor POC Phone" value={editForm.vendorPocPhone} onChange={v => setEditField('vendorPocPhone', v)} />
                <Field label="Vendor POC Email" value={editForm.vendorPocEmail} onChange={v => setEditField('vendorPocEmail', v)} />
              </div>
            )}

            <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Bank details (for refund)</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <Field label="Bank Name" value={editForm.bankName} onChange={v => setEditField('bankName', v)} />
              <Field label="IFSC Code" value={editForm.bankIfsc} onChange={v => setEditField('bankIfsc', v)} />
              <Field label="Account Holder Name" value={editForm.bankAccountName} onChange={v => setEditField('bankAccountName', v)} />
              <Field label="Account Number" value={editForm.bankAccountNumber} onChange={v => setEditField('bankAccountNumber', v)} />
              <Field label="Branch / Address" value={editForm.bankAddress} onChange={v => setEditField('bankAddress', v)} span />
            </div>

            <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Deposit amount</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:12, marginBottom:20 }}>
              <Field label="Deposit Amount (₹)" type="number" value={editForm.depositAmount} onChange={v => setEditField('depositAmount', v)} />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={closeEdit} disabled={savingEdit}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={saveEdit}
                disabled={savingEdit}
                style={{ background: '#7c5cfc', borderColor: '#7c5cfc', opacity: savingEdit ? 0.6 : 1 }}
              >
                {savingEdit ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}